import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { PublicHolidayListResource } from '@/types/resources';
import { api } from '@/utils/api';

import PublicHolidayListFormModal from './PublicHolidayListFormModal';

interface Props {
  list: PublicHolidayListResource;
}

const PublicHolidayListsTableRow: FC<Props> = ({ list }) => {
  const [showEditModal, setShowEditModal] = useState(false);

  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this public holiday list?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/payments/freelancers/holidays/public-holiday-lists/${list.id}`,
      );

      await queryClient.invalidateQueries(['public-holiday-lists'], {
        exact: false,
      });

      alerts.success('Public holiday list was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <>
      <tr>
        <Td>{list.name}</Td>
        <Td>{list.holidays?.length || 0} days</Td>
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowEditModal(true)}
              >
                <span>
                  <PencilIcon />
                </span>
                Edit
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsWrap>
        </Td>
      </tr>
      <AnimatePresence>
        {showEditModal && (
          <PublicHolidayListFormModal
            list={list}
            onClose={() => {
              setShowEditModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PublicHolidayListsTableRow;
